import { useEffect, useRef, useState } from 'react';
import { createLogStream } from '../realtime/sse/createLogStream';
import { agentLogLines } from '../mocks/agentDetail';

const MAX_BUFFER = 100;
const MOCK_INTERVAL_MS = 2500;

// Streams log lines for one agent. Tries the SSE endpoint first; when no
// backend is reachable (EventSource error) it degrades to a synthetic mock
// stream so the Live tab stays demonstrable. Subscribes on mount, tears down
// everything on unmount (T6.5/T6.13 deep-link restore).
function useAgentLogStream(agentId) {
  const [entries, setEntries] = useState([]);
  const [sourceKind, setSourceKind] = useState('connecting');
  const streamRef = useRef(null);
  const fallbackRef = useRef(null);
  const lineIndexRef = useRef(0);

  useEffect(() => {
    setEntries([]);
    lineIndexRef.current = 0;

    const push = (entry) => {
      setEntries((previous) => [...previous.slice(-(MAX_BUFFER - 1)), entry]);
    };

    const startMockStream = () => {
      setSourceKind('mock');
      const lines = agentLogLines(agentId);

      fallbackRef.current = setInterval(() => {
        const text = lines[lineIndexRef.current % lines.length];
        lineIndexRef.current += 1;
        push({ id: `log-${Date.now()}`, text, at: new Date().toISOString(), level: 'info' });
      }, MOCK_INTERVAL_MS);
    };

    try {
      setSourceKind('live');
      streamRef.current = createLogStream(`/api/agents/${agentId}/logs`, {
        onMessage: (message) => push({ id: `log-${Date.now()}`, ...message }),
        onError: () => {
          streamRef.current?.close();
          streamRef.current = null;
          startMockStream();
        }
      });
    } catch {
      startMockStream();
    }

    return () => {
      streamRef.current?.close();
      streamRef.current = null;

      if (fallbackRef.current) {
        clearInterval(fallbackRef.current);
        fallbackRef.current = null;
      }
    };
  }, [agentId]);

  return { entries, sourceKind };
}

export default useAgentLogStream;
